import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useDispatch } from 'react-redux';
import PrivateRoute from '../components/PrivateRoute';
import MainLayout from '../components/Layout';
import { MENU } from '../constants/permissions';
import { reconnectWebSocket } from '../store/slices/userSlice';


// 懒加载组件
const Dashboard = lazy(() => import('../pages/Dashboard'));
const Login = lazy(() => import('../pages/Login'));
const UserManagement = lazy(() => import('../pages/UserManagement'));
const RoleManagement = lazy(() => import('../pages/RoleManagement'));
const PermissionManagement = lazy(() => import('../pages/PermissionManagement'));
const OperationLog = lazy(() => import('../pages/OperationLog'));
const SystemOperationLog = lazy(() => import('../pages/SystemOperationLog'));
const SystemErrorLog = lazy(() => import('../pages/SystemErrorLog'));
const Settings = lazy(() => import('../pages/Settings'));
const BannerManagement = lazy(() => import('../pages/BannerManagement'));
const NotFound = lazy(() => import('../pages/NotFound'));
const Forbidden = lazy(() => import('../pages/Forbidden'));
const Profile = lazy(() => import('../pages/Profile'));

// 用户管理相关组件
const LoginManagement = lazy(() => import('../pages/LoginManagement'));
const UserInfoManagement = lazy(() => import('../pages/UserInfoManagement'));
const UserAccountManagement = lazy(() => import('../pages/UserAccountManagement'));


// 认证管理组件
const BusinessVerification = lazy(() => import('../pages/VerificationManagement/BusinessVerification'));
const UserVerification = lazy(() => import('../pages/VerificationManagement/UserVerification'));

// 通信管理组件
const NotificationManagement = lazy(() => import('../pages/CommunicationManagement/NotificationManagement'));
const ChatManagement = lazy(() => import('../pages/CommunicationManagement/ChatManagement'));
const PushManagement = lazy(() => import('../pages/CommunicationManagement/PushManagement'));
const FeedbackManagement = lazy(() => import('../pages/CommunicationManagement/FeedbackManagement'));
// 财务组件
// const OrderManagement = lazy(() => import('../pages/FinanceManagement/OrderManagement'));
const FinanceManagement = lazy(() => import('../pages/FinanceManagement'));
const WithdrawManagement = lazy(() => import('../pages/FinanceManagement/WithdrawManagement'));
const TransactionRecord = lazy(() => import('../pages/FinanceManagement/TransactionRecord'));

const PaymentIntegration = lazy(() => import('../pages/FinanceManagement/PaymentIntegration'));
const FreezeRecordManagement = lazy(() => import('../pages/FinanceManagement/FreezeRecordManagement'));
const PaymentOrderManagement = lazy(() => import('../pages/FinanceManagement/OrderManagement'));

// 店铺管理
const ShopManagement = lazy(() => import('../pages/ProductManagement/ShopManagement'));
// 店铺访问统计
const StoreVisitStatistics = lazy(() => import('../pages/ProductManagement/ShopManagement/StoreVisitStatistics'));
// 商品管理
const ProductManagement = lazy(() => import('../pages/ProductManagement/ProductManagement'));
// 物流管理
const LogisticsManagement = lazy(() => import('../pages/LogisticsManagement'));
const OrderManagement = lazy(() => import('../pages/OrderManagement'));
// 发货中心
const ShippingCenter = lazy(() => import('../pages/OrderManagement/ShippingCenter'));
// 质检中心
const QualityCenter = lazy(() => import('../pages/OrderManagement/QualityCenter'));
// 售后服务
const AfterSaleServiceManagement = lazy(() => import('../pages/OrderManagement/AfterSaleServiceManagement'));
// 服务管理
const CustomServiceManagement = lazy(() => import('../pages/ServiceManagement/CustomServiceManagement'));
const AccompanyServiceManagement = lazy(() => import('../pages/ServiceManagement/AccompanyServiceManagement'));

// 求购与动态组件
const PurchaseAndDynamics = lazy(() => import('../pages/CommunicationManagement/PurchaseAndDynamics'));

// 定制服务请求管理
const CustomServiceRequestManagement = lazy(() => import('../pages/ServiceManagement/CustomServiceRequestManagement'));

// 平台代售管理（包含订单和商品）
const PlatformSalesManagement = lazy(() => import('../pages/ServiceManagement/PlatformSalesManagement'));

const HelpCenter = lazy(() => import('../pages/CommunicationManagement/HelpCenter'));

// 购物车管理
const ShoppingCartManagement = lazy(() => import('../pages/ProductManagement/ShoppingCartManagement'));
// 地址管理
const UserAddressManagement = lazy(() => import('../pages/UserManagement/UserAddressManagement'));

// 点赞管理
const LikeRecordManagement = lazy(() => import('../pages/CommunicationManagement/LikeRecordManagement'));

// 互动消息管理
const InteractionMessageManagement = lazy(() => import('../pages/CommunicationManagement/InteractionMessageManagement'));

// 玉石鉴定服务管理
const JadeIdentificationServiceManagement = lazy(() => import('../pages/JadeIdentificationServiceManagement'));

// 资讯管理
const ArticleManagement = lazy(() => import('../pages/ArticleManagement'));
const ArticleTypeManagement = lazy(() => import('../pages/ArticleTypeManagement'));
const ArticleRedeemCodeManagement = lazy(() => import('../pages/ArticleRedeemCodeManagement'));
const UserArticleRedeemManagement = lazy(() => import('../pages/UserArticleRedeemManagement'));
// 玉石鉴定知识文章管理
const JadeIdentificationArticleManagement = lazy(() => import('../pages/JadeIdentificationArticleManagement'));
const CreateJadeIdentificationArticle = lazy(() => import('../pages/JadeIdentificationArticleManagement/CreateArticle'));
// 玉石鉴定分类管理
const JadeIdentificationCategoryManagement = lazy(() => import('../pages/JadeIdentificationCategoryManagement'));
// 玉石鉴定订单管理
const JadeIdentificationOrderManagement = lazy(() => import('../pages/JadeIdentificationOrderManagement'));

// 加载中组件
const LoadingComponent = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" tip="页面加载中..." />
  </div>
);

// 路由配置
const AppRouter = () => {
  const dispatch = useDispatch();

  // 应用初始化时尝试重连 WebSocket
  useEffect(() => {
    const reconnectWs = async () => {
      try {
        await dispatch(reconnectWebSocket()).unwrap();
      } catch (error) {
        console.warn('WebSocket 重连失败:', error);
      }
    };

    reconnectWs();
  }, [dispatch]);

  return (
    <Suspense fallback={<LoadingComponent />}>
      <Routes>
        {/* 登录页 */}
        <Route path="/login" element={<Login />} />

        {/* 无权限页面 */}
        <Route path="/403" element={<Forbidden />} />

        {/* 仪表盘 - 独立路由，不在Layout内 */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute requiredPermissions={MENU.DASHBOARD}>
              <Dashboard />
            </PrivateRoute>
          }
        />

        {/* 主布局内的路由 */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }
        >
          {/* 默认重定向到系统用户管理 */}
          <Route index element={<Navigate to="/system/users" replace />} />

          {/* 系统管理 */}
          {/* 用户管理 */}
          <Route
            path="/system/users"
            element={
              <PrivateRoute requiredPermissions={MENU.SYSTEM_USER}>
                <UserManagement />
              </PrivateRoute>
            }
          />

          {/* 角色管理 */}
          <Route
            path="/system/roles"
            element={
              <PrivateRoute requiredPermissions={MENU.SYSTEM_ROLE}>
                <RoleManagement />
              </PrivateRoute>
            }
          />

          {/* 权限管理 */}
          <Route
            path="/system/permissions"
            element={
              <PrivateRoute requiredPermissions={MENU.SYSTEM_PERMISSION}>
                <PermissionManagement />
              </PrivateRoute>
            }
          />

          {/* 操作日志 */}
          <Route
            path="/system/logs"
            element={
              <PrivateRoute requiredPermissions={MENU.SYSTEM_LOG}>
                <OperationLog />
              </PrivateRoute>
            }
          />

          {/* 系统操作日志 */}
          <Route
            path="/system/operation-log"
            element={
              <PrivateRoute requiredPermissions={MENU.SYSTEM_OPERATION_LOG}>
                <SystemOperationLog />
              </PrivateRoute>
            }
          />

          {/* 系统错误日志 */}
          <Route
            path="/system/error-log"
            element={
              <PrivateRoute requiredPermissions={MENU.SYSTEM_ERROR_LOG}>
                <SystemErrorLog />
              </PrivateRoute>
            }
          />

          {/* 系统设置 */}
          <Route
            path="/system/settings"
            element={
              <PrivateRoute requiredPermissions={MENU.SYSTEM_SETTINGS}>
                <Settings />
              </PrivateRoute>
            }
          />

          {/* 轮播图管理 */}
          <Route
            path="/banner/management"
            element={
              <PrivateRoute requiredPermissions={MENU.BANNER_MANAGEMENT}>
                <BannerManagement />
              </PrivateRoute>
            }
          />

          {/* 个人中心 - 通用功能，不需要权限控制 */}
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />

         

          {/* 财务管理 */}
          <Route
            path="/reward/finance"
            element={
              <PrivateRoute requiredPermissions={MENU.REWARD_FINANCE}>
                <FinanceManagement />
              </PrivateRoute>
            }
          />

          {/* 提现管理 */}
          <Route
            path="/reward/finance/withdraw"
            element={
              <PrivateRoute requiredPermissions={MENU.REWARD_FINANCE_WITHDRAW}>
                <WithdrawManagement />
              </PrivateRoute>
            }
          />

          {/* 交易记录 */}
          <Route
            path="/reward/finance/transaction"
            element={
              <PrivateRoute requiredPermissions={MENU.REWARD_FINANCE_TRANSACTION}>
                <TransactionRecord />
              </PrivateRoute>
            }
          />

          {/* 财务订单管理 */}
          <Route
            path="/reward/finance/order"
            element={
              <PrivateRoute requiredPermissions={MENU.REWARD_FINANCE_ORDER}>
                <PaymentOrderManagement />
              </PrivateRoute>
            }
          />

       

          {/* 账户冻结记录管理 */}
          <Route
            path="/reward/finance/freeze-record"
            element={
              <PrivateRoute requiredPermissions={MENU.REWARD_FINANCE_FREEZE_RECORD}>
                <FreezeRecordManagement />
              </PrivateRoute>
            }
          />
          {/* 支付接口集成   */}
          <Route
            path="/reward/finance/payment"
            element={
              <PrivateRoute requiredPermissions={MENU.REWARD_FINANCE_PAYMENT}>
                <PaymentIntegration />
              </PrivateRoute>}
          />
          {/* 认证管理 */}
          <Route path="/reward/verification">
            {/* 默认重定向到企业认证 */}
            <Route index element={<Navigate to="/reward/verification/business" replace />} />

            {/* 企业认证管理 */}
            <Route
              path="business"
              element={
                <PrivateRoute requiredPermissions={MENU.REWARD_VERIFICATION_BUSINESS}>
                  <BusinessVerification />
                </PrivateRoute>
              }
            />

            {/* 用户认证管理 */}
            <Route
              path="user"
              element={
                <PrivateRoute requiredPermissions={MENU.REWARD_VERIFICATION_USER}>
                  <UserVerification />
                </PrivateRoute>
              }
            />
          </Route>

          {/* 用户账户管理 */}
          <Route
            path="/reward/account"
            element={
              <PrivateRoute requiredPermissions={MENU.REWARD_ACCOUNT}>
                <UserAccountManagement />
              </PrivateRoute>
            }
          />

        

          {/* 登录管理 */}
          <Route
            path="/reward/login"
            element={
              <PrivateRoute requiredPermissions={MENU.REWARD_LOGIN}>
                <LoginManagement />
              </PrivateRoute>
            }
          />

          {/* 用户信息管理 */}
          <Route
            path="/reward/user-info"
            element={
              <PrivateRoute requiredPermissions={MENU.REWARD_USER}>
                <UserInfoManagement />
              </PrivateRoute>
            }
          />

          {/* 通信管理路由 */}
          {/* 系统通知管理 */}
          <Route
            path="/communication/notifications"
            element={
              <PrivateRoute requiredPermissions={MENU.COMMUNICATION_NOTIFICATION}>
                <NotificationManagement />
              </PrivateRoute>
            }
          />

          {/* 聊天消息管理 */}
          <Route
            path="/communication/chat"
            element={
              <PrivateRoute requiredPermissions={MENU.COMMUNICATION_CHAT}>
                <ChatManagement />
              </PrivateRoute>
            }
          />

          {/* 消息推送管理 */}
          <Route
            path="/communication/push"
            element={
              <PrivateRoute requiredPermissions={MENU.COMMUNICATION_PUSH}>
                <PushManagement />
              </PrivateRoute>
            }
          />

        {/* 反馈管理 */}
        <Route
            path="/communication/feedback"
            element={
              <PrivateRoute requiredPermissions={MENU.COMMUNICATION_FEEDBACK}>
                <FeedbackManagement />
              </PrivateRoute>
            }
          />

          {/* 帮助中心 */}
          <Route
            path="/help/center"
            element={
              <PrivateRoute requiredPermissions={MENU.HELP_CENTER}>
                <HelpCenter />
              </PrivateRoute>
            }
          />

          {/* 店铺管理 */}
          <Route
            path="/product/shops"
            element={
              <PrivateRoute requiredPermissions={MENU.PRODUCT_SHOP}>
                <ShopManagement />
              </PrivateRoute>
            }
          />

          {/* 店铺访问统计 */}
          <Route
            path="/store/visit/statistics"
            element={
              <PrivateRoute requiredPermissions={MENU.STORE_VISIT_STATISTICS_MENU}>
                <StoreVisitStatistics />
              </PrivateRoute>
            }
          />

          {/* 商品管理 */}
          <Route path="/product/management" element={
            <PrivateRoute requiredPermissions={MENU.PRODUCT_MANAGEMENT}>
              <ProductManagement />
            </PrivateRoute>
          }/>

          {/* 物流管理 */}
          <Route path="/logistics" element={
            <PrivateRoute requiredPermissions={MENU.LOGISTICS_MANAGEMENT}>
              <LogisticsManagement />
            </PrivateRoute>
          }/>

          {/* 订单管理 */}
          <Route path="/order" element={
            <PrivateRoute requiredPermissions={MENU.ORDER_MANAGEMENT}>
              <OrderManagement />
            </PrivateRoute>
          }/>

          {/* 发货中心 */}
          <Route path="/order/shipping" element={
            <PrivateRoute requiredPermissions={MENU.ORDER_SHIPPING}>
              <ShippingCenter />
            </PrivateRoute>
          }/>

          {/* 质检中心 */}
          <Route path="/order/quality" element={
            <PrivateRoute requiredPermissions={MENU.ORDER_QUALITY}>
              <QualityCenter />
            </PrivateRoute>
          }/>
          {/* 售后服务 */}
          <Route path="/order/after-sale" element={
            <PrivateRoute requiredPermissions={MENU.ORDER_QUALITY}>
              <AfterSaleServiceManagement />
            </PrivateRoute>
          }/>

          {/* 求购与动态 */}
          <Route path="/purchase/and/dynamics" element={
            <PrivateRoute requiredPermissions={MENU.PURCHASE_AND_DYNAMICS}>
              <PurchaseAndDynamics />
            </PrivateRoute>
          }/>

           {/* 服务管理路由 */}
        <Route 
          path="/service/custom" 
          element={
            <PrivateRoute permission={MENU.SERVICE_CUSTOM}>
              <CustomServiceManagement />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/service/accompany" 
          element={
            <PrivateRoute permission={MENU.SERVICE_ACCOMPANY}>
              <AccompanyServiceManagement />
            </PrivateRoute>
          } 
        />
        {/* 定制服务请求管理 */}
        <Route 
          path="/service/request" 
          element={
            <PrivateRoute requiredPermissions={MENU.SERVICE_CUSTOM}>
              <CustomServiceRequestManagement />
            </PrivateRoute>
          } 
        />

        {/* 平台代售管理（包含订单和商品） */}
        <Route 
          path="/service/platform-sales" 
          element={
            <PrivateRoute requiredPermissions={[MENU.SERVICE_PLATFORM_SALES]}>
              <PlatformSalesManagement />
            </PrivateRoute>
          } 
        />

        {/* 购物车管理 */}
        <Route path="/shopping/cart" element={
          <PrivateRoute requiredPermissions={MENU.SHOPPING_CART}>
            <ShoppingCartManagement />
          </PrivateRoute>
        }/>
        {/* 玉石鉴定服务管理 */}
        <Route path="/jade-identification-service" element={
          <PrivateRoute requiredPermissions={MENU.JADE_IDENTIFICATION_SERVICE}>
            <JadeIdentificationServiceManagement />
          </PrivateRoute>
        }/>
        {/* 地址管理 */}
        <Route path="/user/address" element={
          <PrivateRoute requiredPermissions={MENU.USER_ADDRESS}>
            <UserAddressManagement />
          </PrivateRoute>
        }/>
        {/* 点赞管理 */}
        <Route path="/like/management" element={
          <PrivateRoute requiredPermissions={MENU.LIKE_MANAGEMENT}>
            <LikeRecordManagement />
          </PrivateRoute>
        }/>
        {/* 互动消息管理 */}
        <Route path="/interaction/message" element={
          <PrivateRoute requiredPermissions={MENU.INTERACTION_MESSAGE}>
            <InteractionMessageManagement />
          </PrivateRoute>
        }/>
        
        {/* 资讯管理 */}
        <Route path="/article/management/list" element={
          <PrivateRoute requiredPermissions={MENU.ARTICLE_MANAGEMENT}>
            <ArticleManagement />
          </PrivateRoute>
        }/>
        
        {/* 文章类型管理 */}
        <Route path="/article/type/management" element={
          <PrivateRoute requiredPermissions={MENU.ARTICLE_TYPE_MANAGEMENT}>
            <ArticleTypeManagement />
          </PrivateRoute>
        }/>

        {/* 玉石鉴定知识文章管理 */}
        <Route path="/jade-identification-article/management" element={
          <PrivateRoute requiredPermissions={MENU.JADE_IDENTIFICATION_ARTICLE_MANAGEMENT}>
            <JadeIdentificationArticleManagement />
          </PrivateRoute>
        }/>
        
        {/* 新增玉石鉴定知识文章 */}
        <Route path="/jade-identification-article/create" element={
          <PrivateRoute requiredPermissions={MENU.JADE_IDENTIFICATION_ARTICLE_MANAGEMENT}>
            <CreateJadeIdentificationArticle />
          </PrivateRoute>
        }/>

        {/* 玉石鉴定分类管理 */}
        <Route path="/jade-identification-category/management" element={
          <PrivateRoute requiredPermissions={MENU.JADE_IDENTIFICATION_CATEGORY}>
            <JadeIdentificationCategoryManagement />
          </PrivateRoute>
        }/>
        
        {/* 玉石鉴定订单管理 */}
        <Route path="/jade-identification-order/management" element={
          <PrivateRoute requiredPermissions={MENU.JADE_IDENTIFICATION_ORDER}>
            <JadeIdentificationOrderManagement />
          </PrivateRoute>
        }/>
        
        {/* 付费文章兑换码管理 */}
        <Route path="/article/redeem-code" element={
          <PrivateRoute requiredPermissions={MENU.ARTICLE_REDEEM_CODE}>
            <ArticleRedeemCodeManagement />
          </PrivateRoute>
        }/>
        
        {/* 用户付费文章服务兑换管理 */}
        <Route path="/user-article-redeem" element={
          <PrivateRoute requiredPermissions={MENU.USER_ARTICLE_REDEEM}>
            <UserArticleRedeemManagement />
          </PrivateRoute>
        }/>
        </Route>

        {/* 404页面 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter; 